import { useQuery } from '@apollo/client/react';
import type { Route } from 'next';

import { ImageModule } from '#components/Image/ImageModule';
import { useCurrentDate } from '#cutils/context/TimeContext';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import { TrackEventDataType } from '#types/tracking';
import { articleIsBreakingNews } from '#utils/article/articleIsBreakingNews';
import { logger } from '#utils/logging/logger';

import { ARTICLE_LIST_ITEM_SIZES, ArticleListItem } from './Article/Items/ArticleListItem';
import styles from './TopArticles.module.scss';
import { TopArticlesDocument } from './__generated__/TopArticles.generated';

export function TopArticles() {
	const { data, error, loading } = useQuery(TopArticlesDocument);
	const currentDate = useCurrentDate();

	if (loading) {
		return null;
	}

	if (error) {
		logger.error(`Error loading top articles: %s`, error.message);
		return null;
	}

	if (!data) {
		logger.error(`No data returned from 'TopArticles'.`);
		return null;
	}

	const topArticles = data.topArticles?.nodes;

	if (!topArticles) {
		logger.error(`No data returned from 'TopArticles'.`);
		return null;
	}

	const mappedTopArticles = topArticles.filter(notNullOrUndefined).map((topArticle) => {
		const isBreakingNews = articleIsBreakingNews(topArticle, currentDate);

		const modulesTypes = topArticle.modulesTypes.filter(notNullOrUndefined);

		const imageComp = <ImageModule image={topArticle.image} sizes={ARTICLE_LIST_ITEM_SIZES} />;

		return (
			<ArticleListItem
				key={topArticle.rowId}
				className={styles.article}
				as="h3"
				image={imageComp}
				isBreakingNews={isBreakingNews}
				modulesTypes={modulesTypes}
				internalPath={topArticle.path as Route}
				publicationDate={topArticle.publicationDate}
				teaserText={topArticle.teaserText}
				title={topArticle.title}
				trackEventDataType={TrackEventDataType.TopArticle}
			/>
		);
	});

	return (
		<section className={styles.topArticlesRoot}>
			<h2 className={`heading3 ${styles.title}`}>Neuste Artikel</h2>

			{mappedTopArticles}
		</section>
	);
}
